import type React from 'react';

export type WidgetSize = 'S' | 'M' | 'L' | 'XL' | 'FULL';

/** A backend `@RequiresTask`: any one of these task ids opens the endpoint. */
export type TaskRequirement = readonly number[];

/**
 * The reader's enabled task ids with their ancestors, as `GET /tasks` and the
 * backend read them: a child task opens its parent's endpoints.
 */
export type WidgetAccess = ReadonlySet<number>;

/**
 * How much of a row a size is worth, relative to the sizes beside it.
 *
 * Not a column count: the layout spends all twelve columns of a row in these
 * proportions (`overview/model/pack.ts`), so an `M` next to another `M` is
 * half a row and an `M` next to a `FULL` is a quarter of one.
 */
export const WIDGET_SIZE_WEIGHT: Record<WidgetSize, number> = {
  S: 1,
  M: 2,
  L: 3,
  XL: 4,
  FULL: 6,
};

export const WIDGET_SIZE_ORDER: WidgetSize[] = ['S', 'M', 'L', 'XL', 'FULL'];

export type ModuleId =
  | 'cash-position'
  | 'cash-flow'
  | 'investments'
  | 'debts'
  | 'transactions'
  | 'transfers'
  | 'reports'
  | 'closing'
  | 'tariffs'
  | 'policies'
  | 'links'
  | 'fx'
  | 'insights';

export interface DateRange {
  from: string;
  to: string;
}

export type PeriodPreset =
  | 'last7'
  | 'last30'
  | 'last90'
  | 'currentMonth'
  | 'previousMonth'
  | 'yearToDate';

export const PERIOD_PRESETS: PeriodPreset[] = [
  'last7', 'last30', 'last90', 'currentMonth', 'previousMonth', 'yearToDate',
];

export type TagMatch = 'ANY' | 'ALL';

export const UNTAGGED = '__untagged__';

export type ScopeDimension =
  | 'companies'
  | 'companyTags'
  | 'institutions'
  | 'accounts'
  | 'accountGroups'
  | 'accountTags'
  | 'currency'
  | 'period'
  | 'ownership';

export type TagOption = 'untagged' | 'matchAll';

export const TAG_OPTIONS: TagOption[] = ['untagged', 'matchAll'];

export const SCOPE_DIMENSIONS: ScopeDimension[] = [
  'companies', 'companyTags', 'institutions', 'accounts', 'accountGroups', 'accountTags', 'currency', 'period',
  'ownership',
];

export interface GlobalScope {
  companyIds: string[];
  institutionIds: string[];
  accountIds: string[];
  companyTagIds: string[];
  accountTagIds: string[];
  accountGroupIds: string[];
  companyTagMatch: TagMatch;
  accountTagMatch: TagMatch;
  withOwnership: boolean;
  currency: string;
  periodPreset: PeriodPreset;
  period: DateRange;
  referenceDate: string;
}

export interface WidgetPins {
  companyIds?: string[];
  institutionIds?: string[];
  accountIds?: string[];
  companyTagIds?: string[];
  companyTagMatch?: TagMatch;
  accountTagIds?: string[];
  accountTagMatch?: TagMatch;
  accountGroupIds?: string[];
  withOwnership?: boolean;
  currency?: string;
  periodPreset?: PeriodPreset;
}

export type LinkQuery = Record<string, string | string[] | number | boolean | undefined | null>;

export interface DrillStep {
  dim: string;
  id: string;
}

export interface WidgetFocus {
  route?: string;
  companyIds?: string[];
  institutionIds?: string[];
  accountIds?: string[];
  accountGroupIds?: string[];
  accountTagIds?: string[];
  companyTagIds?: string[];
  currencies?: string[];
  period?: DateRange;
  date?: string;
  drill?: DrillStep[];
  query?: LinkQuery;
}

export interface ResolvedScope extends GlobalScope {
  pinned: ScopeDimension[];
}

export type WidgetParams = Record<string, string>;

export interface WidgetParamOption {
  value: string;
  label: string;
}

export type WidgetControlKind = 'segmented' | 'select';

export interface WidgetParamDef {
  key: string;
  label: string;
  control: WidgetControlKind;
  options?: WidgetParamOption[];
  /**
   * Options the app has to go and fetch — the saved cash-flow views, for one. Called as a hook
   * while the control renders, so it can query; whatever it returns replaces `options`.
   */
  useOptions?: () => WidgetParamOption[];
  defaultValue: string;
  hiddenBelow?: WidgetSize;
}

export interface WidgetInstance {
  instanceId: string;
  widgetId: string;
  size: WidgetSize;
  params?: WidgetParams;
  pins?: WidgetPins;
  titleOverride?: string;
}

export interface WidgetRenderContext {
  /**
   * How much to draw, not how wide the box is.
   *
   * The two used to be the same number. Now the row works out the width and
   * hands the widget back the nearest size to it, so a panel stretched to
   * fill a row is told it is bigger and shows more, rather than being told a
   * width it never read.
   */
  size: WidgetSize;
  scope: ResolvedScope;
  /** The overview-wide scope, untouched by pins and by `ignoresScope`. */
  globalScope: GlobalScope;
  params: WidgetParams;
  instance: WidgetInstance;
  preview: boolean;
  routePrefix?: string;
  /**
   * Route of the module that owns the widget, already prefixed and carrying the
   * widget's scope and parameters as the module's own filters; absent in preview.
   */
  moduleHref?: string;
  linkTo: (focus?: WidgetFocus) => string | undefined;
}

export interface WidgetProps {
  ctx: WidgetRenderContext;
}

export interface WidgetDefinition {
  id: string;
  module: ModuleId;
  title: string;
  description: string;
  sizes: WidgetSize[];
  defaultSize: WidgetSize;
  params?: WidgetParamDef[];
  ignoresScope?: boolean;
  ignoredDimensions?: ScopeDimension[];
  /**
   * Tag refinements the widget's endpoints cannot express — "without tag" and
   * "carrying every tag". The widget still filters by the tags themselves.
   */
  unsupportedTagOptions?: TagOption[];
  /**
   * Gates on top of its module's (`MODULE_GATES`): the endpoints the widget
   * reads that those do not open, or a backend module it needs (`model/gates.ts`).
   */
  requiresTasks?: TaskRequirement[];
  moduleRoute?: string;
  linkFocus?: (params: WidgetParams, scope: ResolvedScope) => WidgetFocus;
  keywords?: string[];
  component: React.FC<WidgetProps>;
}
